import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl, leadsFromApiPayload, LEADS_PAGE_SIZE } from '@/lib/apiBase';
import { escapeHTML, escapeHTMLWithBreaks } from '@/lib/escapeHtml';
import { useAuth } from '../context/AuthContext';
import { applyDocumentSeo, SEO_KEYWORDS_PRIMARY } from '../components/SeoHead';

const READ_STORAGE_KEY = 'leadlegale_lista_read_ids_v3';

function prettyTipo(tipo) {
  if (tipo === 'malasanita') return 'Malasanita';
  if (tipo === 'stradale') return 'Incidente';
  return tipo || '-';
}

function prettyGravita(value) {
  if (value === 'alta') return 'Alta';
  if (value === 'bassa') return 'Bassa';
  return 'Media';
}

function statusBadgeStyle(status) {
  const value = String(status || 'nuovo');
  if (value === 'pratica_aperta') return { backgroundColor: '#10B981', color: '#FFFFFF' };
  if (value === 'in_contatto') return { backgroundColor: '#F59E0B', color: '#111827' };
  if (value === 'in_attesa_doc') return { backgroundColor: '#8B5CF6', color: '#FFFFFF' };
  if (value === 'non_fattibile_chiuso') return { backgroundColor: '#64748B', color: '#FFFFFF' };
  return { backgroundColor: '#3B82F6', color: '#FFFFFF' };
}

function prettyStatus(status) {
  const map = {
    nuovo: 'Nuovo',
    in_contatto: 'In Contatto',
    in_attesa_doc: 'In Attesa Doc',
    pratica_aperta: 'Pratica Aperta',
    non_fattibile_chiuso: 'Non Fattibile / Chiuso',
  };
  return map[String(status || 'nuovo')] || 'Nuovo';
}

function getLeadId(lead) {
  return String(lead?.id || lead?._id || '');
}

export function FastListaPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [assigningLeadId, setAssigningLeadId] = useState('');
  const [assignmentSavedLeadId, setAssignmentSavedLeadId] = useState('');
  const [expandedId, setExpandedId] = useState('');
  const [readLeadIds, setReadLeadIds] = useState(() => {
    try {
      const raw = window.localStorage.getItem(READ_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const sortedLeads = useMemo(() => {
    if (!Array.isArray(leads)) return [];
    return [...leads].sort((a, b) => {
      const da = Date.parse(a?.created_at || '') || 0;
      const db = Date.parse(b?.created_at || '') || 0;
      return db - da;
    });
  }, [leads]);

  const isLawyer = ['lawyer', 'partner'].includes(String(user?.role || '').toLowerCase());
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const token = useMemo(
    () => String(window.localStorage.getItem('leadlegale_token') || '').trim(),
    [user?.id, user?.role, user?.email]
  );

  const markAsRead = (leadId) => {
    if (!leadId) return;
    setReadLeadIds((prev) => {
      if (prev.includes(leadId)) return prev;
      const next = [...prev, leadId];
      try {
        window.localStorage.setItem(READ_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore localStorage errors.
      }
      return next;
    });
  };

  const loadLeads = async () => {
    setIsLoading(true);
    setError('');
    try {
      const aggregated = [];
      let skip = 0;
      let total = 0;
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
      };
      for (;;) {
        const { data } = await axios.get(apiUrl('/api/leads'), {
          headers,
          params: { limit: LEADS_PAGE_SIZE, skip, t: Date.now() },
        });
        const page = leadsFromApiPayload(data);
        total = typeof data?.total === 'number' ? data.total : page.length;
        aggregated.push(...page);
        if (!data?.has_more || page.length === 0 || aggregated.length >= total) break;
        skip += page.length;
      }
      setLeads(aggregated);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Errore nel caricamento lead.');
      setLeads([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadLawyers = async () => {
    try {
      const { data } = await axios.get(
        `${apiUrl('/api/users/lawyers')}?t=${Date.now()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Cache-Control': 'no-store, no-cache, must-revalidate, max-age=0',
            Pragma: 'no-cache',
            Expires: '0',
          },
        }
      );
      setLawyers(Array.isArray(data) ? data : []);
    } catch {
      setLawyers([]);
    }
  };

  const assignLead = async (leadId, lawyerId) => {
    if (!isAdmin || !leadId) return;
    const assignedValue = lawyerId || null;
    setAssigningLeadId(String(leadId));
    setAssignmentSavedLeadId('');
    try {
      await axios.patch(
        apiUrl(`/api/assign-lead/${leadId}`),
        { lawyer_id: assignedValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      setLeads((prev) =>
        Array.isArray(prev)
          ? prev.map((lead) => (String(getLeadId(lead)) === String(leadId) ? { ...lead, assigned_to: assignedValue } : lead))
          : prev
      );
      setAssignmentSavedLeadId(String(leadId));
      setTimeout(() => setAssignmentSavedLeadId(''), 1800);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Errore durante assegnazione lead.');
    } finally {
      setAssigningLeadId('');
    }
  };

  useEffect(() => {
    if (!loading && user && token) {
      loadLeads();
    }
  }, [loading, user?.id, user?.role, token]);

  useEffect(() => {
    if (!loading && user && token && isAdmin) {
      loadLawyers();
    }
  }, [loading, user?.id, user?.role, token, isAdmin]);

  useEffect(() => {
    if (loading) return;
    if (!user || !token) {
      navigate('/login?next=/lista', { replace: true });
    }
  }, [loading, user, token, navigate]);

  useEffect(() => {
    const baseDesc =
      'Lista lead Lead Legale per professionisti: infortunistica stradale, risarcimento danni e consulenza legale. Area riservata.';
    if (loading) {
      applyDocumentSeo({
        title: 'Caricamento | Lista lead – Lead Legale',
        description: baseDesc,
        keywords: SEO_KEYWORDS_PRIMARY,
        path: '/lista',
        noindex: true,
      });
      return;
    }
    if (!user || !token) {
      applyDocumentSeo({
        title: 'Lista lead | Accedi – Lead Legale',
        description:
          'Accedi per consultare i lead assegnati. Piattaforma per infortunistica stradale, risarcimento danni e consulenza legale.',
        keywords: SEO_KEYWORDS_PRIMARY,
        path: '/lista',
        noindex: true,
      });
      return;
    }
    applyDocumentSeo({
      title: 'Lista lead | Lead Legale',
      description: baseDesc,
      keywords: SEO_KEYWORDS_PRIMARY,
      path: '/lista',
      noindex: true,
    });
  }, [loading, user, token]);

  if (loading) {
    return (
      <div className="min-h-screen px-3 py-4" style={{ backgroundColor: '#0A192F', color: '#F8FAFC' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-slate-300">Verifica accesso...</p>
        </div>
      </div>
    );
  }

  if (!user || !token) return null;

  return (
    <div className="min-h-screen px-3 py-4" style={{ backgroundColor: '#0A192F', color: '#F8FAFC' }}>
      <div className="max-w-6xl mx-auto">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Lista Lead</h1>
            <p className="text-sm text-slate-300">
              Account: {user?.name || user?.email} ({String(user?.role || '').toUpperCase() || 'USER'})
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadLeads}
              className="rounded-lg px-4 py-2 font-semibold"
              style={{ backgroundColor: '#1E293B', color: '#E5E7EB', border: '1px solid #334155' }}
            >
              Aggiorna
            </button>
            <button
              onClick={async () => {
                await logout();
                navigate('/login?next=/lista', { replace: true });
              }}
              className="rounded-lg px-4 py-2 font-semibold"
              style={{ backgroundColor: '#7F1D1D', color: '#FEE2E2', border: '1px solid #B91C1C' }}
            >
              Logout
            </button>
          </div>
        </div>

        {isLoading && <p className="text-sm text-slate-300">Caricamento lead...</p>}
        {error && <p className="text-sm" style={{ color: '#FCA5A5' }}>{error}</p>}
        {!isLoading && sortedLeads.length === 0 && (
          <p className="text-sm text-slate-300">
            {isLawyer ? 'Nessun lead assegnato al tuo account.' : 'Nessun lead trovato'}
          </p>
        )}

        <div className="md:hidden space-y-3">
          {sortedLeads.map((lead) => {
            const leadId = getLeadId(lead);
            const isRead = readLeadIds.includes(leadId);
            return (
              <div
                key={leadId}
                className="rounded-xl p-4"
                style={{
                  backgroundColor: '#1E293B',
                  border: isRead ? '1px solid #334155' : '1px solid #64748B',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    const nextOpen = expandedId !== leadId;
                    setExpandedId(nextOpen ? leadId : '');
                    if (nextOpen) markAsRead(leadId);
                  }}
                  className="w-full text-left"
                >
                  <div className="lead-row" style={{ borderBottom: 'none', padding: '0.75rem 0' }}>
                    <div className={`score-badge ${(Number.isFinite(Number(lead.score)) ? Number(lead.score) : 0) >= 80 ? 'score-top' : 'score-normal'}`}>
                      {Number.isFinite(Number(lead.score)) ? lead.score : 0}
                    </div>

                    <div style={{ flexGrow: 1 }}>
                      <div style={{ fontSize: '16px', fontWeight: '600' }} className="flex items-center justify-between gap-2 flex-wrap">
                        <span style={{ color: '#F8FAFC' }}>{lead.nome} {lead.cognome}</span>
                        <span className="text-xs font-semibold px-2 py-1 rounded-full shrink-0" style={statusBadgeStyle(lead.stato)}>
                          {prettyStatus(lead.stato)}
                        </span>
                      </div>
                      <div className="lead-mini-info">
                        {prettyGravita(lead.gravita)}
                      </div>
                    </div>

                    <div style={{ color: '#A0ABC0', fontSize: '12px' }}>
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString('it-IT') : '—'}
                    </div>
                  </div>
                </button>

                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-slate-100">{prettyTipo(lead.tipo_incidente)}</span>
                </div>

                <div className="mt-3 flex gap-2">
                  <a
                    href={`tel:${lead.telefono || ''}`}
                    className="flex-1 rounded-lg py-2 text-center font-semibold"
                    style={{ backgroundColor: '#2563EB', color: '#FFFFFF' }}
                  >
                    Chiama
                  </a>
                  <a
                    href={`mailto:${lead.email || ''}`}
                    className="flex-1 rounded-lg py-2 text-center font-semibold"
                    style={{ backgroundColor: '#334155', color: '#E2E8F0' }}
                  >
                    Email
                  </a>
                </div>

                {isAdmin && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: '#CBD5E1' }}>
                      Assegna Avvocato
                    </label>
                    <select
                      value={lead.assigned_to || ''}
                      onChange={(e) => assignLead(leadId, e.target.value)}
                      disabled={assigningLeadId === leadId}
                      className="w-full rounded-xl px-3 py-3 text-sm font-medium"
                      style={{
                        backgroundColor: '#0F172A',
                        color: '#F8FAFC',
                        border: '1px solid rgba(148,163,184,0.35)',
                        minHeight: '46px',
                      }}
                    >
                      <option value="">Non assegnato</option>
                      {lawyers.map((lawyer) => (
                        <option key={lawyer.id} value={lawyer.id}>
                          {(lawyer.name || lawyer.username || lawyer.email)}{lawyer.practice_category ? ` [${lawyer.practice_category}]` : ''}
                        </option>
                      ))}
                    </select>
                    {assignmentSavedLeadId === leadId && (
                      <p className="text-xs mt-2" style={{ color: '#22C55E' }}>
                        Assegnazione salvata
                      </p>
                    )}
                  </div>
                )}

                <div
                  className="mt-3 rounded-lg p-3 text-sm"
                  style={{
                    backgroundColor: '#0F172A',
                    maxHeight: expandedId === leadId ? '420px' : '0',
                    opacity: expandedId === leadId ? 1 : 0,
                    overflow: 'hidden',
                    paddingTop: expandedId === leadId ? '0.75rem' : '0',
                    paddingBottom: expandedId === leadId ? '0.75rem' : '0',
                    transition: 'max-height 240ms ease, opacity 220ms ease, padding 220ms ease',
                  }}
                >
                  {expandedId === leadId && (
                    <>
                    <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Dettaglio completo modulo</p>
                    <p className="text-slate-200"><strong>Nome:</strong> {lead.nome} {lead.cognome}</p>
                    <p className="text-slate-200"><strong>Telefono:</strong> {lead.telefono || '-'}</p>
                    <p className="text-slate-200"><strong>Email:</strong> {lead.email || '-'}</p>
                    <p className="text-slate-200"><strong>Data sinistro:</strong> {lead.data_sinistro || '-'}</p>
                    <p className="text-slate-200"><strong>Luogo:</strong> {lead.luogo || '-'}</p>
                    <p className="text-slate-200"><strong>Ha avvocato:</strong> {lead.ha_avvocato || '-'}</p>
                    <p className="text-slate-200"><strong>Gravita danni:</strong> {lead.gravita_danni || '-'}</p>
                    <p className="text-slate-200"><strong>Responsabilita:</strong> {lead.responsabilita || '-'}</p>
                    <p className="text-slate-200"><strong>Referto medico:</strong> {lead.ha_referto_medico || '-'}</p>
                    <p className="text-slate-200"><strong>Autorita intervenute:</strong> {lead.autorita_intervenute || '-'}</p>
                    <p className="text-slate-200">
                      <strong>Descrizione:</strong>{' '}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: escapeHTMLWithBreaks(lead.descrizione || 'Nessun dettaglio fornito.'),
                        }}
                      />
                    </p>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="hidden md:block rounded-xl overflow-hidden mt-2" style={{ border: '1px solid #334155' }}>
          <table className="w-full text-sm">
            <thead style={{ backgroundColor: '#1E293B' }}>
              <tr>
                <th className="text-left p-3 text-slate-300">Lead</th>
                <th className="text-left p-3 text-slate-300">Tipo</th>
                <th className="text-left p-3 text-slate-300">Stato</th>
                <th className="text-left p-3 text-slate-300">Contatti</th>
                <th className="text-left p-3 text-slate-300">Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {sortedLeads.map((lead) => {
                const leadId = getLeadId(lead);
                return (
                  <tr key={leadId} style={{ borderTop: '1px solid #334155', backgroundColor: '#0F172A' }}>
                    <td className="p-3 align-top">
                      <div className="lead-row" style={{ borderBottom: 'none', padding: '0.5rem 0', cursor: 'default' }}>
                        <div className={`score-badge ${(Number.isFinite(Number(lead.score)) ? Number(lead.score) : 0) >= 80 ? 'score-top' : 'score-normal'}`}>
                          {Number.isFinite(Number(lead.score)) ? lead.score : 0}
                        </div>

                        <div style={{ flexGrow: 1 }}>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#F1F5F9' }}>
                            {lead.nome} {lead.cognome}
                          </div>
                          <div className="lead-mini-info">
                            {prettyGravita(lead.gravita)}
                          </div>
                        </div>

                        <div style={{ color: '#A0ABC0', fontSize: '12px' }}>
                          {lead.created_at ? new Date(lead.created_at).toLocaleDateString('it-IT') : '—'}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-slate-200">{prettyTipo(lead.tipo_incidente)}</td>
                    <td className="p-3">
                      <span className="text-xs font-semibold px-2 py-1 rounded-full" style={statusBadgeStyle(lead.stato)}>
                        {prettyStatus(lead.stato)}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <a href={`tel:${lead.telefono || ''}`} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#2563EB', color: '#FFFFFF' }}>Chiama</a>
                        <a href={`mailto:${lead.email || ''}`} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#334155', color: '#E2E8F0' }}>Email</a>
                      </div>
                    </td>
                    <td className="p-3 text-slate-300 max-w-xl truncate">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: escapeHTML(lead.descrizione || '-'),
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default FastListaPage;
